import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { ReactFlow, Position, Handle, SmoothStepEdge, BezierEdge } from '@xyflow/react';
import { WindowSizeContext } from '/src/App';

import '@xyflow/react/dist/style.css';
import ClimateIconGrid from './ClimateIconGrid/ClimateIconGrid';
import HouseCenter from './HouseCenter/HouseCenter';
import FinancialImpactBars from './FinancialImpactBars/FinancialImpactBars';
import './style.css';

// FlowTrigger ensures DOM is ready before React Flow initializes
const FlowTrigger = ({ onMount }) => {
    useEffect(() => {
        console.log('🚀 FlowTrigger mounted, calling onMount');
        onMount();
    }, [onMount]);
    return <div style={{ display: 'none' }} />;
};

// Custom node types for React Flow - each has specific Handle configuration
const GridCenterNode = () => {
    return (
        <div style={{ width: 5, height: 5, background: 'transparent' }}>
            <Handle type="source" position={Position.Bottom} style={{ visibility: 'hidden' }} />
        </div>
    );
};

const HouseTopNode = () => {
    return (
        <div style={{ width: 5, height: 5, background: 'transparent' }}>
            <Handle type="target" position={Position.Top} style={{ visibility: 'hidden' }} />
        </div>
    );
};

const HouseBottomNode = () => {
    return (
        <div style={{ width: 5, height: 5, background: 'transparent' }}>
            <Handle type="source" position={Position.Bottom} style={{ visibility: 'hidden' }} />
        </div>
    );
};

const ImpactBarNode = () => {
    return (
        <div style={{ width: 5, height: 5, background: 'transparent' }}>
            <Handle type="target" position={Position.Top} style={{ visibility: 'hidden' }} />
        </div>
    );
};

const nodeTypes = {
    gridCenterNode: GridCenterNode,
    houseTopNode: HouseTopNode,
    houseBottomNode: HouseBottomNode,
    impactBarNode: ImpactBarNode
};

const ClimateToCostGraphic = () => {

    const { deviceType } = useContext(WindowSizeContext);

    // All available climate risks
    const climateRisksArray = [
        { id: 0, icon_dark: "Cold-Black.png", icon_light: "Cold-White.png" },
        { id: 1, icon_dark: "Convection-Black.png", icon_light: "Convection-White.png" },
        { id: 2, icon_dark: "Drought-Black.png", icon_light: "Drought-White.png" },
        { id: 3, icon_dark: "Earthquake-Black.png", icon_light: "Earthquake-White.png" },
        { id: 4, icon_dark: "Wildfire-Black.png", icon_light: "Wildfire-White.png" },
        { id: 5, icon_dark: "Flood-Black.png", icon_light: "Flood-White.png" },
        { id: 6, icon_dark: "Hail-Black.png", icon_light: "Hail-White.png" },
        { id: 7, icon_dark: "Hurricane-Black.png", icon_light: "Hurricane-White.png" },
        { id: 8, icon_dark: "Landslide-Black.png", icon_light: "Landslide-White.png" },
        { id: 9, icon_dark: "Lightning-Black.png", icon_light: "Lightning-White.png" },
        { id: 10, icon_dark: "Sinkhole-Black.png", icon_light: "Sinkhole-White.png" },
        { id: 11, icon_dark: "Snow-Black.png", icon_light: "Snow-White.png" },
        { id: 12, icon_dark: "Tornado-Black.png", icon_light: "Tornado-White.png" },
        { id: 13, icon_dark: "Tsunami-Black.png", icon_light: "Tsunami-White.png" },
        { id: 14, icon_dark: "Wind-Black.png", icon_light: "Wind-White.png" },
    ];

    // State for displayed risks (5 risks with their risk levels)
    const [displayedRisks, setDisplayedRisks] = useState([]);

    // State for impact bar values (3 bars, values 0-10)
    const [impactValues, setImpactValues] = useState([5, 5, 5]);

    // React Flow state
    const [showFlow, setShowFlow] = useState(false);
    const [flowReady, setFlowReady] = useState(false);
    const [nodes, setNodes] = useState([]);
    const [edges, setEdges] = useState([]);

    // Edge types for React Flow (must be memoized to prevent re-creation)
    const edgeTypes = useMemo(() => ({
        smoothstep: SmoothStepEdge
    }), []);

    // Get element position relative to container
    const getElementPosition = useCallback((element) => {
        const container = document.querySelector('.climate-to-cost-graphic-container');
        if (!element || !container) {
            // console.log('⚠️ getElementPosition: element or container not found', { element: !!element, container: !!container });
            return { x: 0, y: 0 };
        }

        const containerRect = container.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();

        const position = {
            x: elementRect.left - containerRect.left + (elementRect.width / 2),
            y: elementRect.top - containerRect.top + (elementRect.height / 2)
        };

        return position;
    }, []);

    // Update nodes and edges for React Flow
    const updateFlowElements = useCallback(() => {
        // console.log('🔍 updateFlowElements called');

        const gridCenter = document.getElementById('grid-center-point');
        const houseTop = document.getElementById('house-top-point');
        const houseBottom = document.getElementById('house-bottom-point');
        const impactBar0 = document.getElementById('impact-bar-target-0');
        const impactBar1 = document.getElementById('impact-bar-target-1');
        const impactBar2 = document.getElementById('impact-bar-target-2');

        // console.log('📍 Elements found:', {
        //     gridCenter: !!gridCenter,
        //     houseTop: !!houseTop,
        //     houseBottom: !!houseBottom,
        //     impactBar0: !!impactBar0,
        //     impactBar1: !!impactBar1,
        //     impactBar2: !!impactBar2
        // });

        if (!gridCenter || !houseTop || !houseBottom || !impactBar0 || !impactBar1 || !impactBar2) {
            console.log('❌ Not all elements found, returning early');
            return;
        }

        const gridCenterPos = getElementPosition(gridCenter);
        const houseTopPos = getElementPosition(houseTop);
        const houseBottomPos = getElementPosition(houseBottom);
        const impactBar0Pos = getElementPosition(impactBar0);
        const impactBar1Pos = getElementPosition(impactBar1);
        const impactBar2Pos = getElementPosition(impactBar2);

        // Calculate the vertical center of the house for connections
        const houseCenterY = (houseTopPos.y + houseBottomPos.y) / 2;
        const houseIncomingPos = { x: houseTopPos.x, y: houseCenterY };
        const houseOutgoingPos = { x: houseBottomPos.x, y: houseCenterY };

        // console.log('📐 Positions calculated:', {
        //     gridCenterPos,
        //     houseTopPos,
        //     houseBottomPos,
        //     houseCenterY,
        //     houseIncomingPos,
        //     houseOutgoingPos,
        //     impactBar0Pos,
        //     impactBar1Pos,
        //     impactBar2Pos
        // });

        // Create nodes with specific types for each connection point
        const newNodes = [
            {
                id: 'grid-center',
                type: 'gridCenterNode',
                position: gridCenterPos,
                data: {}
            },
            {
                id: 'house-incoming',
                type: 'houseTopNode',
                position: houseIncomingPos,
                data: {}
            },
            {
                id: 'house-outgoing',
                type: 'houseBottomNode',
                position: houseOutgoingPos,
                data: {}
            },
            {
                id: 'impact-bar-0',
                type: 'impactBarNode',
                position: impactBar0Pos,
                data: {}
            },
            {
                id: 'impact-bar-1',
                type: 'impactBarNode',
                position: impactBar1Pos,
                data: {}
            },
            {
                id: 'impact-bar-2',
                type: 'impactBarNode',
                position: impactBar2Pos,
                data: {}
            }
        ];

        // What color should the lines be? Stroke width? Dash array?
        const lineColor = 'var(--light-grey)';
        const lineWidth = 2;
        const lineDasharray = 4;

        // Create edges - all connect to center of house
        const newEdges = [
            // Grid center to house center (incoming)
            {
                id: 'edge-grid-house',
                source: 'grid-center',
                target: 'house-incoming',
                type: 'bezier',
                animated: true,
                style: {
                    stroke: lineColor,
                    strokeWidth: lineWidth,
                    strokeLinecap: 'round',
                    strokeDasharray: lineDasharray,
                    zIndex: -1
                }
            },
            // House center (outgoing) to impact bars
            {
                id: 'edge-house-impact0',
                source: 'house-outgoing',
                target: 'impact-bar-0',
                type: 'bezier',
                animated: true,
                style: {
                    stroke: lineColor,
                    strokeWidth: lineWidth,
                    strokeLinecap: 'round',
                    strokeDasharray: lineDasharray,
                    zIndex: -1
                }
            },
            {
                id: 'edge-house-impact1',
                source: 'house-outgoing',
                target: 'impact-bar-1',
                type: 'bezier',
                animated: true,
                style: {
                    stroke: lineColor,
                    strokeWidth: lineWidth,
                    strokeLinecap: 'round',
                    strokeDasharray: lineDasharray,
                    zIndex: -1
                }
            },
            {
                id: 'edge-house-impact2',
                source: 'house-outgoing',
                target: 'impact-bar-2',
                type: 'bezier',
                animated: true,
                style: {
                    stroke: lineColor,
                    strokeWidth: lineWidth,
                    strokeLinecap: 'round',
                    strokeDasharray: lineDasharray,
                    zIndex: -1
                }
            }
        ];

        // console.log('✅ Setting nodes:', newNodes.length, 'nodes');
        // console.log('✅ Setting edges:', newEdges.length, 'edges');
        // console.log('Nodes:', newNodes);
        // console.log('Edges:', newEdges);

        if (newNodes.length > 0) {
            setNodes(newNodes);
        }
        if (newEdges.length > 0) {
            setEdges(newEdges);
            setFlowReady(true);
            // console.log('✅ Flow is ready!');
        }
    }, [getElementPosition]);

    // Update displayed risks and impact values every 4 seconds
    useEffect(() => {
        const updateValues = () => {
            // Randomly select 5 unique risks from the 15 available
            const shuffled = [...climateRisksArray].sort(() => 0.5 - Math.random());
            const selectedRisks = shuffled.slice(0, 5);

            // Randomly decide how many should be red (1, 2, or 3)
            const numRed = Math.floor(Math.random() * 3) + 1; // 1, 2, or 3

            // Create the displayed risks array with risk levels
            // First numRed icons are red (level 1), rest are yellow (level 2)
            const risksWithLevels = selectedRisks.map((risk, index) => ({
                ...risk,
                riskLevel: index < numRed ? 1 : 2
            }));

            setDisplayedRisks(risksWithLevels);

            // Generate new random values for impact bars (2-10, never 0)
            const newImpactValues = [
                Math.floor(Math.random() * 9) + 2,  // 2-10
                Math.floor(Math.random() * 9) + 2,  // 2-10
                Math.floor(Math.random() * 9) + 2   // 2-10
            ];
            setImpactValues(newImpactValues);
        };

        // Run immediately
        updateValues();

        // Then run every 4 seconds
        const interval = setInterval(updateValues, 3000);

        return () => clearInterval(interval);
    }, []);

    // Update React Flow when showFlow becomes true
    useEffect(() => {
        // console.log('🎬 showFlow useEffect triggered. showFlow =', showFlow);
        if (showFlow) {
            setFlowReady(false);
            // console.log('⏰ Scheduling updateFlowElements in 100ms');
            // Delay to ensure DOM is fully rendered
            const timeout = setTimeout(() => {
                // console.log('⏰ 100ms delay complete, calling updateFlowElements');
                updateFlowElements();
            }, 100);

            // Update on window resize
            const handleResize = () => {
                updateFlowElements();
            };

            window.addEventListener('resize', handleResize);

            return () => {
                clearTimeout(timeout);
                window.removeEventListener('resize', handleResize);
            };
        }
    }, [showFlow, updateFlowElements]);

    // console.log('🎨 Rendering ClimateToCostGraphic. showFlow =', showFlow, 'flowReady =', flowReady, 'nodes =', nodes.length, 'edges =', edges.length);

    return (
        <div className={`climate-to-cost-graphic-container ${deviceType}`}>
            {/* ReactFlow must be rendered BEFORE content */}
            {(showFlow && flowReady && nodes.length > 0 && edges.length > 0) && (
                <div className="reactflow-wrapper">
                    {/* {console.log('✨ ReactFlow is rendering with', nodes.length, 'nodes and', edges.length, 'edges')} */}
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        nodeTypes={nodeTypes}
                        edgeTypes={edgeTypes}
                        fitView={false}
                        nodesDraggable={false}
                        nodesConnectable={false}
                        zoomOnScroll={false}
                        panOnScroll={false}
                        preventScrolling={true}
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            pointerEvents: 'none',
                            zIndex: 1
                        }}
                    />
                </div>
            )}

            <ClimateIconGrid displayedRisks={displayedRisks} />

            <HouseCenter />

            <FinancialImpactBars impactValues={impactValues} />

            {/* FlowTrigger must be at the END */}
            <FlowTrigger onMount={() => {
                // console.log('🔄 FlowTrigger onMount callback called, setting showFlow to true');
                setShowFlow(true);
            }} />
        </div>
    );
};

export default ClimateToCostGraphic;
